import { useAuth } from '@/hooks/useAuth';
import { getUserShortName } from '@/lib/user';
import { ArrowLeft, Search, Store, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationBell } from './notifications/NotificationBell';
import { InstallAppButton } from './pwa/InstallAppButton';

interface HeaderProps {
  showBack?: boolean;
  title?: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const Header = ({ showBack, title, showSearch = false, searchValue, onSearchChange }: HeaderProps) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const accountHref = isAuthenticated ? '/perfil' : '/entrar';
  const accountLabel = isAuthenticated ? getUserShortName(user) : 'Entrar';

  return (
    <header className="border-border bg-background/95 sticky top-0 z-40 border-b backdrop-blur-sm">
      <div className="container flex h-14 items-center justify-between gap-3 md:h-16">
        {showBack ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:bg-muted -ml-2 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
            aria-label="Voltar"
          >
            <ArrowLeft className="text-foreground h-5 w-5" />
          </button>
        ) : null}

        <Link to="/" className="flex shrink-0 items-center gap-2">
          <div className="bg-primary flex h-8 w-8 items-center justify-center rounded-lg">
            <span className="text-primary-foreground text-sm font-bold">VT</span>
          </div>
          <span className="text-foreground hidden text-lg font-bold sm:block">Vira Tempo</span>
        </Link>

        {title && <h1 className="text-foreground flex-1 truncate text-center text-base font-semibold">{title}</h1>}

        {showSearch && (
          <div className="mx-2 max-w-md flex-1">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <input
                type="text"
                aria-label="Buscar estabelecimento"
                placeholder="Onde você quer agendar?"
                value={searchValue || ''}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="bg-muted text-foreground placeholder:text-muted-foreground focus:ring-primary/30 h-10 w-full rounded-xl border-none pr-4 pl-9 text-sm transition-all outline-none focus:ring-2"
              />
            </div>
          </div>
        )}

        {!title && <div className="flex-1" />}

        <div className="flex shrink-0 items-center gap-2">
          <a
            href="https://admin.viratempo.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground hidden h-10 items-center gap-1.5 rounded-xl px-4 text-sm font-medium transition-colors sm:flex"
          >
            <Store className="h-4 w-4" />
            Cadastrar meu negócio
          </a>
          {isLoading ? (
            <div className="bg-muted h-10 w-10 rounded-xl sm:w-28" aria-hidden="true" />
          ) : (
            <>
              <InstallAppButton />
              <NotificationBell />
              <Link
                to={accountHref}
                className="bg-muted text-foreground hover:bg-muted/80 flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium transition-colors sm:px-4"
              >
                <User className="h-4 w-4 shrink-0" />
                <span
                  className={isAuthenticated ? 'hidden max-w-24 truncate sm:inline sm:max-w-28' : 'hidden sm:inline'}
                >
                  {accountLabel}
                </span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
